/**
 * 跨天未签退内联提示(规则2 UI)。展开态 = inline card;收起态 = rail 按钮(attention
 * 圆点)+ 点开 flyout。表单:可编辑下班时间(默认自动算)+ Resolve & clock out。
 * 失败内联可重试、绝不静默(Bug Ownership)。Spec §4.5。
 */
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { AlertTriangle } from "lucide-react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "./clock-panel.css";
import { toClockErrorMessage } from "./useClockActions";
import type { CrossDayDetected } from "./crossDay";

dayjs.extend(utc);
dayjs.extend(timezone);

export interface CrossDayPromptProps {
  collapsed: boolean;
  attendanceDate: string; // YYYY-MM-DD
  detected: CrossDayDetected;
  defaultClockOutIso: string;
  userTz: string;
  /** 收到「输入时间(用户时区)→UTC ISO」;throw 由本组件内联展示。 */
  onResolve: (targetIso: string) => Promise<void>;
}

const DETECTED_KEY: Record<CrossDayDetected, string> = {
  break: "attendanceClock.crossDay.onBreak",
  activity: "attendanceClock.crossDay.activity",
  forgot: "attendanceClock.crossDay.forgot",
};

function ResolveCard(props: CrossDayPromptProps) {
  const { t } = useTranslation("shifts");
  const { attendanceDate, detected, defaultClockOutIso, userTz, onResolve } = props;
  const [editedValue, setValue] = useState<string | null>(null);
  const value = editedValue ?? dayjs(defaultClockOutIso).tz(userTz).format("YYYY-MM-DDTHH:mm");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const dateLabel = dayjs.tz(attendanceDate, userTz).format("MMM D");

  const submit = async () => {
    setValue(value);
    setSubmitting(true);
    setError(null);
    try {
      const iso = dayjs.tz(value, userTz).utc().toISOString();
      await onResolve(iso);
      // 成功后父组件对账重拉 → crossDay 变 null → 本组件卸载，无需复位
    } catch (e) {
      console.error(
        "[cross-day] resolve failed:",
        (e as { response?: { data?: unknown } })?.response?.data ?? e,
      );
      setError(toClockErrorMessage(e));
      setSubmitting(false);
    }
  };

  return (
    <div className="nvc-crossday">
      <div className="nvc-crossday-hd">
        <AlertTriangle size={13} />
        <span>{t("attendanceClock.crossDay.title")} · {dateLabel}</span>
      </div>
      <div className="nvc-crossday-sub">{t(DETECTED_KEY[detected])}</div>
      <label className="nvc-crossday-field">
        <span>{t("attendanceClock.crossDay.clockOutLabel")}</span>
        <input
          type="datetime-local"
          value={value}
          disabled={submitting}
          onChange={(e) => setValue(e.target.value)}
        />
      </label>
      {error && <div className="nvc-crossday-err">{error}</div>}
      <button className="nvc-btn green" disabled={submitting} onClick={submit}>
        {submitting
          ? t("attendanceClock.crossDay.resolving")
          : t("attendanceClock.crossDay.resolve")}
      </button>
    </div>
  );
}

export function CrossDayPrompt(props: CrossDayPromptProps) {
  const { t } = useTranslation("shifts");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    if (!open) return undefined;
    const h = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [open]);

  if (!props.collapsed) {
    return (
      <div className="nvc-clock nvc-rel">
        <ResolveCard {...props} />
      </div>
    );
  }

  return (
    <div className="nvc-rel" style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 3 }}>
      <button
        className="nvc-railbtn st-break"
        aria-label={t("attendanceClock.crossDay.title")}
        onClick={() => setOpen((o) => !o)}
      >
        <AlertTriangle size={17} />
        <span className="nvc-cornerdot amber" />
      </button>
      {open && (
        <div className="nvc-flyout rail">
          <ResolveCard {...props} />
        </div>
      )}
    </div>
  );
}
